import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { FaEnvelope, FaPhone, FaGithub, FaLinkedin, FaMapMarkerAlt } from 'react-icons/fa';
import emailjs from '@emailjs/browser';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const Contact = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // EmailJS configuration
    // Configure these environment variables in a .env file in the root directory:
    // REACT_APP_EMAILJS_SERVICE_ID=your_service_id
    // REACT_APP_EMAILJS_TEMPLATE_ID=your_template_id
    // REACT_APP_EMAILJS_PUBLIC_KEY=your_public_key
    // 
    // Get these values from https://www.emailjs.com/:
    // 1. Create an account and add an Email Service (Gmail, Outlook, etc.)
    // 2. Create an Email Template with variables: {{from_name}}, {{from_email}}, {{message}}, {{to_name}}
    // 3. Get your Public Key from Account > API Keys
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    // Validate configuration
    if (!serviceId || !templateId || !publicKey || 
        serviceId === 'YOUR_SERVICE_ID' || 
        templateId === 'YOUR_TEMPLATE_ID' || 
        publicKey === 'YOUR_PUBLIC_KEY') {
      toast.error(t('contact.emailjsNotConfigured'), {
        duration: 6000,
      });
      setIsSubmitting(false);
      return;
    }

    // Template parameters - these must match your EmailJS template variables
    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      message: formData.message,
      to_name: 'Pedro Thyrso',
    };

    try {
      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      // Show success message
      toast.success(t('contact.success'), {
        duration: 5000,
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        message: '',
      });
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(t('contact.error'), {
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: FaEnvelope,
      labelKey: 'labels.email',
      value: 'dev.thyrso@gmail.com',
      link: 'mailto:dev.thyrso@gmail.com',
    },
    {
      icon: FaPhone,
      labelKey: 'labels.phone',
      value: '+55 (14) 99682-5201',
      link: 'tel:+5514996825201',
    },
    {
      icon: FaMapMarkerAlt,
      labelKey: 'labels.location',
      value: 'Brazil',
      link: null,
    },
    {
      icon: FaGithub,
      labelKey: 'labels.github',
      value: 'github.com/devthyrso',
      link: 'https://github.com/devthyrso',
    },
    {
      icon: FaLinkedin,
      labelKey: 'labels.linkedin',
      value: 'linkedin.com/in/pedro-thyrso',
      link: 'https://linkedin.com/in/pedro-thyrso',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  return (
    <section id="contact" className="py-20 relative">
      <div className="container mx-auto px-6" ref={ref}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold text-center mb-16 bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent"
          >
            {t('contact.title')}
          </motion.h2>

          <motion.div variants={itemVariants} className="mb-12">
            <p className="text-lg text-gray-300 leading-relaxed text-center max-w-3xl mx-auto">
              {t('contact.description')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto items-start">
            {/* Contact Information */}
            <motion.div variants={itemVariants} className="space-y-4">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  return (
                    <motion.div
                      key={index}
                      whileHover={{ x: 5 }}
                      className="glass p-4 rounded-lg flex items-center space-x-4 glow-hover"
                    >
                      <div className="bg-blue-500 bg-opacity-20 p-3 rounded-lg">
                        <Icon className="text-2xl text-blue-500" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">{t(`contact.${info.labelKey}`)}</p>
                        {info.link ? (
                          <a
                            href={info.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-300 hover:text-blue-500 transition-colors duration-300"
                          >
                            {info.value}
                          </a>
                        ) : (
                          <p className="text-gray-300">{info.value}</p>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
            </motion.div>

            {/* Contact Form */}
            <motion.div variants={itemVariants} className="flex flex-col">
              <form onSubmit={handleSubmit} className="space-y-6 w-full">
                <div>
                  <label htmlFor="name" className="block text-gray-300 mb-2">
                    {t('contact.name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-transparent border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 text-white transition-colors duration-300"
                    placeholder={t('contact.namePlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-gray-300 mb-2">
                    {t('contact.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-transparent border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 text-white transition-colors duration-300"
                    placeholder={t('contact.emailPlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-gray-300 mb-2">
                    {t('contact.message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-3 bg-transparent border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 text-white resize-none transition-colors duration-300"
                    placeholder={t('contact.messagePlaceholder')}
                  ></textarea>
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full px-8 py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 transition-all duration-300 glow disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t('contact.sending') : t('contact.sendMessage')}
                </motion.button>
              </form>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
